/**
 * MM-5490 — application handoffs.
 *
 * The backend hands team-admin an opaque, single-use handoff URL. team-admin never copies that URL
 * verbatim: it validates it, extracts the code and the documented public flags, and rebuilds the
 * destination it opens. Anything that is not an allowlisted public flag is dropped, so a malformed
 * or tampered response cannot put credentials — or any other unvouched value — into a browser tab,
 * its history, or its referrer.
 */

export const HANDOFF_PATHNAME = '/handoff';
export const ADMIN_HANDOFF_VERSION = 2;
export const HANDOFF_FAILURE_MESSAGE = 'Unable to start that session. Please try again.';

export class InvalidHandoffResponseError extends Error {}
export class HandoffWindowBlockedError extends Error {}

export function handoffFailureCategory(error: any): string {
  if (error instanceof InvalidHandoffResponseError) {
    return 'invalid-response';
  }
  if (error instanceof HandoffWindowBlockedError) {
    return 'window-blocked';
  }
  return error?.status === 403 ? 'policy-denied' : 'request-failed';
}

const MIN_HANDOFF_CODE_LENGTH = 32;
const MAX_HANDOFF_CODE_LENGTH = 128;
const MAX_HANDOFF_URL_LENGTH = 16_384;

/**
 * Query parameters the backend is allowed to place on a handoff URL. They are public launch flags
 * and carry no secret. A handoff URL containing anything else is rejected rather than opened.
 */
export const HANDOFF_PUBLIC_FLAGS: readonly string[] = ['supportMode', 'create', 'guidedSignup'];

/**
 * Flags team-admin itself may add to the destination. `api` selects the backend environment for a
 * local/dev launch and is chosen by team-admin, never by the response.
 */
const CALLER_FLAGS: readonly string[] = [...HANDOFF_PUBLIC_FLAGS, 'api'];

/**
 * Hosts a handoff URL may point at. All Wallit-operated app and partner brand origins live under
 * `wallit.app`; `localhost` covers local development where the apps are served over plain HTTP.
 */
const ALLOWED_HANDOFF_DOMAIN = 'wallit.app';
const ALLOWED_LOCAL_HOSTNAMES: readonly string[] = ['localhost', '127.0.0.1'];
const ALLOWED_CONFIGURED_HOSTNAMES: readonly string[] = ['wallit-team-dev.web.app'];

export interface HandoffLaunch {
  /** Origin the backend selected for this handoff (the v3app or partner brand origin). */
  origin: string;
  /** The opaque, single-use handoff code. */
  code: string;
  /** Public launch flags the backend set, restricted to {@link HANDOFF_PUBLIC_FLAGS}. */
  flags: Record<string, string>;
}

export interface LegacyHandoffLaunch {
  /** Validated origin selected by the backend for v3app. */
  origin: string;
  /** Exact pre-existing credential route, beginning with /token/. */
  tokenPath: string;
}

export type AdminHandoffSelection =
  | {mode: 'opaque'; handoff: HandoffLaunch}
  | {mode: 'legacy'; legacy: LegacyHandoffLaunch};

function isAllowedHandoffOrigin(url: URL): boolean {
  const hostname = url.hostname.toLowerCase();

  if (ALLOWED_LOCAL_HOSTNAMES.includes(hostname)) {
    return url.protocol === 'http:' || url.protocol === 'https:';
  }

  if (url.protocol !== 'https:') {
    return false;
  }

  return ALLOWED_CONFIGURED_HOSTNAMES.includes(hostname)
    || hostname === ALLOWED_HANDOFF_DOMAIN
    || hostname.endsWith(`.${ALLOWED_HANDOFF_DOMAIN}`);
}

function hasOnlyOrigin(rawOrigin: string, url: URL): boolean {
  return rawOrigin === url.origin || rawOrigin === url.origin + '/';
}

function isValidOpaqueCode(code: string): boolean {
  const allowedCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  return code.length >= MIN_HANDOFF_CODE_LENGTH
    && code.length <= MAX_HANDOFF_CODE_LENGTH
    && Array.from(code).every((character) => allowedCharacters.includes(character));
}

/**
 * Validates a backend-supplied handoff URL. Returns null — never a partially trusted value — when
 * the URL is missing, malformed, points somewhere unexpected, or carries anything beyond one
 * non-empty opaque code and the documented public flags.
 */
export function parseHandoffUrl(rawUrl: unknown): HandoffLaunch | null {
  if (typeof rawUrl !== 'string' || !rawUrl.trim() || rawUrl.length > MAX_HANDOFF_URL_LENGTH) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (url.username || url.password || url.hash) {
    return null;
  }

  if (!isAllowedHandoffOrigin(url) || url.pathname !== HANDOFF_PATHNAME) {
    return null;
  }

  const codes = url.searchParams.getAll('code');
  if (codes.length !== 1 || !isValidOpaqueCode(codes[0])) {
    return null;
  }

  const flags: Record<string, string> = {};
  for (const key of url.searchParams.keys()) {
    if (key === 'code') {
      continue;
    }
    if (!HANDOFF_PUBLIC_FLAGS.includes(key)) {
      return null;
    }
    flags[key] = url.searchParams.get(key) ?? '';
  }

  return {origin: url.origin, code: codes[0], flags};
}

/**
 * Parses the explicit numeric-v2 admin response. Legacy fields are accepted only when the backend
 * selected legacy mode; mixed responses are rejected. A failed opaque launch never downgrades.
 */
export function parseAdminHandoffResponse(rawResponse: unknown): AdminHandoffSelection | null {
  if (!rawResponse || typeof rawResponse !== 'object') {
    return null;
  }

  const response = rawResponse as Record<string, unknown>;
  if (response.handoffVersion !== ADMIN_HANDOFF_VERSION) {
    return null;
  }

  if (response.mode === 'opaque') {
    if (response.url !== undefined || response.v3Url !== undefined) {
      return null;
    }
    const handoff = parseHandoffUrl(response.handoffUrl);
    return handoff ? {mode: 'opaque', handoff} : null;
  }

  if (response.mode === 'legacy') {
    if (response.handoffUrl !== undefined || !isValidLegacyAdminUrl(response.url)) {
      return null;
    }
    const legacy = parseLegacyV3Url(response.v3Url);
    return legacy ? {mode: 'legacy', legacy} : null;
  }

  return null;
}

function hasCredentialPair(value: string, prefix: string): boolean {
  if (!value.startsWith(prefix)) {
    return false;
  }
  const credentials = value.slice(prefix.length);
  const separator = credentials.indexOf('/');
  return separator > 0 && separator < credentials.length - 1;
}

function isValidLegacyAdminUrl(rawUrl: unknown): boolean {
  if (typeof rawUrl !== 'string' || !rawUrl || rawUrl.length > MAX_HANDOFF_URL_LENGTH) {
    return false;
  }

  try {
    const url = new URL(rawUrl);
    return isAllowedHandoffOrigin(url)
      && !url.username
      && !url.password
      && url.pathname === '/'
      && !url.search
      && hasCredentialPair(url.hash, '#/home/fb/');
  } catch {
    return false;
  }
}

function parseLegacyV3Url(rawUrl: unknown): LegacyHandoffLaunch | null {
  if (typeof rawUrl !== 'string' || !rawUrl || rawUrl.length > MAX_HANDOFF_URL_LENGTH) {
    return null;
  }

  try {
    const url = new URL(rawUrl);
    if (!isAllowedHandoffOrigin(url)
      || url.username
      || url.password
      || url.search
      || url.hash
      || !hasCredentialPair(url.pathname, '/token/')) {
      return null;
    }
    return {origin: url.origin, tokenPath: url.pathname};
  } catch {
    return null;
  }
}

function requireConfiguredOrigin(rawOrigin: string): string {
  const url = new URL(rawOrigin);
  if (!hasOnlyOrigin(rawOrigin, url) || !isAllowedHandoffOrigin(url)) {
    throw new Error('Invalid configured handoff origin.');
  }
  return url.origin;
}

/**
 * Rebuilds the destination team-admin opens from a validated handoff plus caller-chosen flags.
 *
 * `targetOrigin` is deliberately independent of `handoff.origin`: the backend picks the v3app or
 * partner brand origin, while a team-admin launch (or a local/dev launch) reconstructs its own
 * destination. Only the validated code and allowlisted flags survive into the result.
 */
export function buildHandoffUrl(
  targetOrigin: string,
  handoff: HandoffLaunch,
  extraFlags: Record<string, string> = {},
): string {
  targetOrigin = requireConfiguredOrigin(targetOrigin);
  const url = new URL(HANDOFF_PATHNAME, `${targetOrigin}`.replace(/\/+$/, '') + '/');
  url.searchParams.set('code', handoff.code);

  Object.entries({...handoff.flags, ...extraFlags})
    .filter(([key, value]) => CALLER_FLAGS.includes(key) && value !== undefined && value !== null)
    .forEach(([key, value]) => url.searchParams.set(key, `${value}`));

  return url.toString();
}

/** Rebuilds only the exact server-selected legacy token route on a configured target origin. */
export function buildLegacyHandoffUrl(
  targetOrigin: string,
  legacy: LegacyHandoffLaunch,
  options: {supportMode?: boolean; api?: string} = {},
): string {
  let tokenPath = legacy.tokenPath;
  if (options.supportMode) {
    tokenPath = '/token/supportMode' + tokenPath.slice('/token'.length);
  }

  const url = new URL(tokenPath, requireConfiguredOrigin(targetOrigin) + '/');
  if (options.api) {
    url.searchParams.set('api', options.api);
  }
  return url.toString();
}
